import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, money, useQuery } from '../api.js';
import { Drawer, ErrorNote, Pane, Panes, Pill, Spinner, useToast } from '../ui.js';
import { MaintenanceSections } from './Maintenance.js';
import { RecordsOnFile } from './Records.js';
import { History } from './History.js';
import type { Reservation } from '../../../shared/types.js';

interface AircraftRow {
  id: number; tail: string; model: string; description: string; hobbs: number;
  rateCents: number; tiedown: string; ifrCapable: boolean; nightCapable: boolean; seats: number;
  status: string; nextInspection: string; nextInspectionHours: number | null;
  weekHours: number; utilization: number; openSquawks: number;
}
interface FleetData {
  aircraft: AircraftRow[];
  canManage: boolean;
  showRates: boolean;
  stats: {
    count: number; airworthy: number; grounded: number;
    bookedPercent: number; bookedHours: number; availableHours: number;
    revenueHours: number; nextInspection: { tail: string; hours: number } | null;
  };
}

export function Fleet(): JSX.Element {
  const { data, loading, error, reload } = useQuery<FleetData>('/fleet');
  const [openId, setOpenId] = useState<number | null>(null);
  /*
   * `/fleet?aircraft=12` opens that airframe. The records screen links here by
   * id rather than describing where to click, so a line in the log about an
   * aeroplane goes to the aeroplane.
   */
  const [params, setParams] = useSearchParams();
  const wanted = Number(params.get('aircraft')) || null;
  useEffect(() => { if (wanted) setOpenId(wanted); }, [wanted]);
  const closeDrawer = () => {
    setOpenId(null);
    if (wanted) {
      const next = new URLSearchParams(params);
      next.delete('aircraft');
      setParams(next, { replace: true });
    }
  };

  if (error) return <ErrorNote message={error} />;
  if (loading && !data) return <Spinner />;
  if (!data) return <Spinner />;
  void loading;


  return (
    <div className="page">
      <div className="card">
        <div className="table__scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Tail</th><th>Type</th><th>Hobbs</th><th>Next inspection</th>
                <th>This week</th><th>Utilization</th>
                {data.showRates && <th>Rate</th>}
                <th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {data.aircraft.map((a) => (
                <tr key={a.id}>
                  <td className="bold">{a.tail}</td>
                  <td>{a.model}{a.description && <span className="muted"> · {a.description}</span>}</td>
                  <td className="num">{a.hobbs.toFixed(1)}</td>
                  <td>{a.nextInspection}</td>
                  <td className="num">{a.weekHours.toFixed(1)} hrs</td>
                  <td>
                    <span className="bar" title={`${a.utilization}%`}>
                      <span style={{
                        width: `${a.utilization}%`,
                        background: a.status === 'Grounded' ? 'var(--muted-4)' : 'var(--accent)',
                      }} />
                    </span>
                  </td>
                  {data.showRates && <td className="num">{money(a.rateCents)}/hr</td>}
                  <td><Pill>{a.status}</Pill></td>
                  <td className="num"><button className="btn btn--link" onClick={() => setOpenId(a.id)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="note">Utilization is booked hours against published field hours, not clock hours.</div>

      {/* Airworthiness belongs with the aircraft, not on a tab of its own. */}
      <div style={{ marginTop: 20 }}>
        <MaintenanceSections />
      </div>

      {openId && (
        <AircraftDrawer
          id={openId}
          canManage={data.canManage}
          onClose={closeDrawer}
          onChanged={reload}
        />
      )}
    </div>
  );
}

interface AircraftDetail {
  aircraft: {
    id: number; tail: string; model: string; description: string; hobbs: number;
    rateCents: number; status: string; tiedown: string;
    ifrCapable: boolean; nightCapable: boolean; seats: number;
  };
  squawks: { id: number; title: string; body: string; status: string; reported_on: string }[];
  inspections: { id: number; kind: string; due_hobbs: number | null; due_on: string | null; note: string; remainingHours: number | null }[];
  upcoming: Reservation[];
  recent: Reservation[];
}

function AircraftDrawer({ id, canManage, onClose, onChanged }: {
  id: number; canManage: boolean; onClose: () => void; onChanged: () => void;
}): JSX.Element {
  const { data, reload } = useQuery<AircraftDetail>(`/fleet/${id}`);
  const navigate = useNavigate();
  const toast = useToast();
  const [hobbs, setHobbs] = useState('');
  const [rate, setRate] = useState('');

  const save = async () => {
    try {
      await api.patch(`/fleet/${id}`, {
        ...(hobbs ? { hobbs: Number(hobbs) } : {}),
        ...(rate ? { rateCents: Math.round(Number(rate) * 100) } : {}),
      });
      toast.ok('Aircraft updated');
      setHobbs(''); setRate('');
      reload(); onChanged();
    } catch (err) {
      toast.error('Could not save', err instanceof Error ? err.message : undefined);
    }
  };

  const a = data?.aircraft;

  return (
    <Drawer
      title={a?.tail ?? 'Aircraft'}
      subtitle={a ? `${a.model} · tie-down ${a.tiedown || '—'}` : undefined}
      onClose={onClose}
      footer={canManage && <button className="btn btn--primary" disabled={!hobbs && !rate} onClick={save}>Save changes</button>}
    >
      {!data && <Spinner />}
      {a && (
        <Panes>
          <Pane title="Airframe" flush>
            <div className="row" style={{ gap: 6, padding: '12px 24px 10px' }}>
              <Pill>{a.status === 'airworthy' ? 'Airworthy' : a.status === 'grounded' ? 'Grounded' : 'Restricted'}</Pill>
              {a.ifrCapable && <Pill tone="blue">IFR</Pill>}
              {a.nightCapable && <Pill tone="blue">Night</Pill>}
            </div>
            <div className="kv"><span>Hobbs</span><span>{a.hobbs.toFixed(1)}</span></div>
            <div className="kv"><span>Rate (wet)</span><span>{money(a.rateCents)}/hr</span></div>
            <div className="kv"><span>Seats</span><span>{a.seats}</span></div>
            <div className="kv"><span>Tie-down</span><span>{a.tiedown || '—'}</span></div>

            {data.inspections.length > 0 && (
              <>
                <div className="pane__head" style={{ paddingLeft: 24, paddingRight: 24 }}>Inspections</div>
                {data.inspections.map((i) => (
                  <div className="kv" key={i.id}>
                    <span style={{ fontWeight: 400 }}>{i.kind}</span>
                    <span style={{ color: i.remainingHours != null && i.remainingHours < 10 ? 'var(--sys-red)' : undefined }}>
                      {i.remainingHours != null ? `${i.remainingHours.toFixed(1)} hrs` : i.due_on ?? '—'}
                    </span>
                  </div>
                ))}
              </>
            )}
          </Pane>

          <Pane title={`Open squawks · ${data.squawks.length}`} flush>
            {data.squawks.length === 0 && <div className="empty" style={{ padding: 20 }}>No open squawks.</div>}
            {data.squawks.map((sq) => (
              <div className="finding" key={sq.id}>
                <span className={`finding__dot ${sq.status === 'grounded' ? 'sev-conflict' : 'sev-review'}`} />
                <span>
                  <span className="finding__title">{sq.title}</span>
                  <span className="finding__body">{sq.body}</span>
                  <span className="finding__ref">{sq.reported_on} · {sq.status}</span>
                </span>
              </div>
            ))}
            <div style={{ padding: '10px 24px' }}>
              <button className="btn btn--link" onClick={() => { onClose(); navigate('/maintenance'); }}>
                Open maintenance
              </button>
            </div>
          </Pane>

          <Pane title="Documents on file" flush>
            <RecordsOnFile aircraftId={id} emptyNote="No paperwork is on file for this airframe." />
          </Pane>

          <Pane title="History" flush>
            <div style={{ padding: '4px 24px 14px' }}>
              <History
                compact
                entity="aircraft"
                entityId={id}
                emptyNote="Nothing has been recorded against this aircraft yet."
              />
            </div>
          </Pane>

          <Pane title={`Next 14 days · ${data.upcoming.length}`} flush>
            {data.upcoming.length === 0 && <div className="empty" style={{ padding: 20 }}>Nothing booked.</div>}
            {data.upcoming.slice(0, 10).map((r) => (
              <div className="kv" key={r.id}>
                <span style={{ fontWeight: 400 }}>
                  {new Date(r.startsAt).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                  {' '}{new Date(r.startsAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                </span>
                <span>{r.studentName || r.notes || r.kind}</span>
              </div>
            ))}

            {canManage && (
              <div style={{ padding: '14px 24px 0' }}>
                <div className="closeout__h" style={{ marginBottom: 10 }}>Correct the record</div>
                <div className="stack">
                  <label className="field">
                    <span>Hobbs</span>
                    <input className="input" inputMode="decimal" placeholder={a.hobbs.toFixed(1)}
                      value={hobbs} onChange={(e) => setHobbs(e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Rate per hour (wet, dollars)</span>
                    <input className="input" inputMode="decimal" placeholder={(a.rateCents / 100).toFixed(2)}
                      value={rate} onChange={(e) => setRate(e.target.value)} />
                  </label>
                </div>
              </div>
            )}
          </Pane>
        </Panes>
      )}
    </Drawer>
  );
}
